import React, { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { BodyText, getCloudinaryUrl, Image, Radio, theme } from '@sg/garnish';
import { DressingWeight, type IngredientModification } from '@sg/graphql-schema';

import { INGREDIENT_CARD_IMAGE_WIDTH } from '../../../../constants';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationDetailsDressingWeight = (
  props: CustomizationDetailsDressingModeProps,
) => {
  const { ingredientModification, activeDressingsDetails, isDisabled, setDressingWeight } = props;

  const { formatMessage } = useIntl();

  // ─────────────────────────────────────────────────────────────────────

  const { id, asset, name } = ingredientModification.ingredient;

  const formattedImageUrl = getCloudinaryUrl(asset.url, { crop: 'scale', width: INGREDIENT_CARD_IMAGE_WIDTH }); // prettier-ignore

  const selectedWeight = useMemo(() => {
    const maybeActiveDressingWeight = activeDressingsDetails.find(
      (dressing) => dressing.id === id,
    )?.weight;

    return maybeActiveDressingWeight ?? DressingWeight.Medium;
  }, [activeDressingsDetails, id]);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const onWeightChange = useCallback(
    (weight: DressingWeight) => {
      setDressingWeight(ingredientModification, weight);
    },
    [ingredientModification, setDressingWeight],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View>
      <View style={styles.header}>
        <Image source={formattedImageUrl} style={styles.image} />

        <BodyText sizeMatch={['18']} style={styles.heading}>
          {name}
        </BodyText>
      </View>

      <Radio.Group itemsPerRow={3} gap={theme.spacing['2']}>
        <Radio.Item<DressingWeight>
          value={DressingWeight.Light}
          isSelected={selectedWeight === DressingWeight.Light}
          onPress={onWeightChange}
          label={formatMessage(messages.label, { weight: DressingWeight.Light })} // prettier-ignore
          accessibilityLabel={formatMessage(messages.a11yLabel, { weight: DressingWeight.Light })} // prettier-ignore
          isDisabled={isDisabled}
          size="sm"
          palette="spinach"
        />

        <Radio.Item<DressingWeight>
          value={DressingWeight.Medium}
          isSelected={selectedWeight === DressingWeight.Medium}
          onPress={onWeightChange}
          label={formatMessage(messages.label, { weight: DressingWeight.Medium })} // prettier-ignore
          accessibilityLabel={formatMessage(messages.a11yLabel, { weight: DressingWeight.Medium })} // prettier-ignore
          isDisabled={isDisabled}
          size="sm"
          palette="spinach"
        />

        <Radio.Item<DressingWeight>
          value={DressingWeight.Heavy}
          isSelected={selectedWeight === DressingWeight.Heavy}
          onPress={onWeightChange}
          label={formatMessage(messages.label, { weight: DressingWeight.Heavy })} // prettier-ignore
          accessibilityLabel={formatMessage(messages.a11yLabel, { weight: DressingWeight.Heavy })} // prettier-ignore
          isDisabled={isDisabled}
          size="sm"
          palette="spinach"
        />
      </Radio.Group>
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  label: {
    defaultMessage: `{weight, select,
      LIGHT {Light}
      MEDIUM {Medium}
      HEAVY {Heavy}
      other {}
    }`,
    description: 'Customization | Dressing details | Dressing weight label',
  },
  a11yLabel: {
    defaultMessage: `{weight, select,
      LIGHT {Select light weight}
      MEDIUM {Select medium weight}
      HEAVY {Select heavy weight}
      other {}
    }`,
    description: 'Customization | Dressing details | Dressing weight a11y label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: theme.spacing['2'],
    marginBottom: theme.spacing['2'],
  },
  image: {
    width: 40,
    height: 40,
    borderRadius: 40,
  },
  heading: {
    flex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type CustomizationDetailsDressingModeProps = {
  ingredientModification: IngredientModification;
  activeDressingsDetails: readonly {
    id: string;
    weight?: DressingWeight;
  }[];
  setDressingWeight: (
    ingredientModification: IngredientModification,
    dressingWeight: DressingWeight,
  ) => void;
  isDisabled?: boolean;
};
